/**
 * 分析ページ専用の POST /api/analysis/search クライアント。
 * デスクのシンプル分析（/api/simple-analytics）とは別エンドポイント。
 */
import { apiPostRequest } from '@/utils/apiClient';
import { notifyCalendarScopeError } from '@/utils/calendarScopeError';
import type { RatioDailyAggregatedItem } from '@/types/AnalysisResultType';
import {
    FreeAnalysisPeriodRejectedError,
    FREE_ANALYSIS_PERIOD_OUT_OF_RANGE_CODE,
} from '@/features/analysis/dayPagination/freeAnalysisPeriodWindow';

export const ANALYSIS_SEARCH_PATH = '/api/analysis/search';

/** レスポンスヘッダ `{@link AnalysisController#ANALYSIS_SEARCH_TRACE_HEADER}` と一致させること */
export const ANALYSIS_SEARCH_TRACE_HEADER = 'X-Analysis-Search-Trace-Id';

export type AnalysisSearchRequestBody = {
    calendarId: number;
    startTime: string;
    endTime: string;
    searchKeywords: string[];
    color: string;
    /** サーバー・監査ログと突き合わせるための相関 ID（検索文言ではない UUID） */
    clientTraceId?: string;
};

/** API が返す日次スライス 1 件のおおまかな形（厳密な Bean 検証はサーバー側） */
type DailyAggJson = {
    date?: string;
    totalTime?: number;
    eventItems?: Array<{ title?: string; totalTime?: number; percentage?: number }>;
};

function parseRatioDailyItemsFromSearchResponse(json: unknown): RatioDailyAggregatedItem[] {
    if (!Array.isArray(json) || json.length === 0) {
        return [];
    }
    const currentData = json[0] as { dailyAggregatedData?: DailyAggJson[] } | undefined;
    const raw = currentData?.dailyAggregatedData;
    if (!Array.isArray(raw)) {
        return [];
    }
    return raw.map((item) => ({
        date: item?.date ?? '',
        totalTime: Number(item?.totalTime || 0),
        eventItems: Array.isArray(item?.eventItems)
            ? item.eventItems.map((ev) => ({
                  title: ev?.title ?? '',
                  totalTime: Number(ev?.totalTime || 0),
                  percentage: Number(ev?.percentage || 0),
              }))
            : [],
    }));
}

/** 分析API 取得成功時のみ。未取得・ログイン転送などは null。 */
export type AnalysisSearchFetchOk = {
    dailyItems: RatioDailyAggregatedItem[];
    serverTraceId: string | null;
    clientTraceIdUsed: string;
};

/**
 * 無料プラン期間外など: null（403 カレンダー権限）もあり得る。401/302 はログインへリダイレクト。
 * @returns 日次配列・相関ヘッダ。取得不能で打ち切った場合は null。
 */
export async function fetchAnalysisSearchDailyAggregated(
    body: AnalysisSearchRequestBody,
    tzHeaders?: Record<string, string>,
): Promise<AnalysisSearchFetchOk | null> {
    const clientTraceIdUsed =
        typeof body.clientTraceId === 'string' && body.clientTraceId.length > 0
            ? body.clientTraceId
            : crypto.randomUUID();

    /** apiPostRequest は 401 のとき転送後に Unauthorized を throw するので、レスポンス分岐とは別に処理する */
    let response: Response;
    try {
        response = await apiPostRequest(
            ANALYSIS_SEARCH_PATH,
            { ...body, clientTraceId: clientTraceIdUsed },
            tzHeaders,
        );
    } catch (e) {
        if (e instanceof Error && e.message === 'Unauthorized') {
            return null;
        }
        throw e;
    }

    if (!response?.ok) {
        if (response?.status === 302) {
            window.location.replace('/login?error=unauthorized');
            return null;
        }
        if (response?.status === 422) {
            try {
                const errBody = await response.json();
                if (errBody?.code === FREE_ANALYSIS_PERIOD_OUT_OF_RANGE_CODE) {
                    throw new FreeAnalysisPeriodRejectedError();
                }
            } catch (e) {
                if (e instanceof FreeAnalysisPeriodRejectedError) {
                    throw e;
                }
            }
        }
        if (response?.status === 403) {
            notifyCalendarScopeError();
            return null;
        }
        throw new Error(`API error: ${response?.status}`);
    }

    const serverTraceId = response.headers?.get?.(ANALYSIS_SEARCH_TRACE_HEADER) ?? null;
    const json = await response.json();
    return {
        dailyItems: parseRatioDailyItemsFromSearchResponse(json),
        serverTraceId,
        clientTraceIdUsed,
    };
}

/** 日付キー YYYY-MM の月ごとに分割（クライアント月次キャッシュ用） */
export function groupDailyItemsByMonthKey(
    dailyItems: RatioDailyAggregatedItem[],
): Map<string, RatioDailyAggregatedItem[]> {
    const byMonth = new Map<string, RatioDailyAggregatedItem[]>();
    for (const d of dailyItems) {
        const mk = d.date.slice(0, 7);
        const arr = byMonth.get(mk) ?? [];
        arr.push(d);
        byMonth.set(mk, arr);
    }
    return byMonth;
}
